export const BRAND_PRODUCT_NAME = "DA / DR 30+ to 60+ & 1,000+ monthly traffic";
export const BRAND_CATEGORY_NAME = "Brand";
export const INDUSTRY_ADDON_NAMES = ["igaming", "crypto", "cannabis"];
export const BRAND_MENTION_STORAGE_KEY = "brand-mentions-checkout";
export const BRAND_MENTION_RESTORE_KEY = "brand-mentions-restore";
export const BRAND_MENTION_QUERY_KEY = "brand_mentions_data";

export function normalizeCatalogName(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/,/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function isBrandMentionProduct(product) {
  return normalizeCatalogName(product?.name) === normalizeCatalogName(BRAND_PRODUCT_NAME);
}

export function isBrandCategory(category) {
  return normalizeCatalogName(category?.name) === normalizeCatalogName(BRAND_CATEGORY_NAME);
}

export function isIndustryAddonName(name) {
  return INDUSTRY_ADDON_NAMES.includes(normalizeCatalogName(name));
}

export function stripBrandMentionProducts(products) {
  return (products || [])
    .filter((product) => !isBrandMentionProduct(product))
    .map((product) => ({
      ...product,
      categories: (product.categories || []).filter((category) => !isBrandCategory(category)),
    }));
}

export function stripIndustryAddons(addons) {
  return (addons || [])
    .filter((addon) => !isIndustryAddonName(addon?.name))
    .map((addon) => ({
      ...addon,
      options: (addon.options || []).filter((option) => !isIndustryAddonName(option?.name)),
    }));
}

export function findBrandMentionProduct(products) {
  return (products || []).find((product) => isBrandMentionProduct(product)) || null;
}

export function findBrandCategory(product) {
  return (product?.categories || []).find((category) => isBrandCategory(category)) || null;
}

export function brandBasePrice(product) {
  const category = findBrandCategory(product);
  const pivotCost = Number(category?.pivot?.cost);
  const productCost = Number(product?.cost);
  if (Number.isFinite(pivotCost) && pivotCost > 0) return pivotCost;
  if (Number.isFinite(productCost) && productCost > 0) return productCost;
  return 300;
}

function industryCost(option, fallback) {
  const cost = Number(option?.cost ?? fallback ?? 250);
  return Number.isFinite(cost) && cost > 0 ? cost : 250;
}

export function collectIndustryAddons(addons) {
  const found = [];
  (addons || []).forEach((addon) => {
    const options = Array.isArray(addon?.options) ? addon.options : [];
    if (isIndustryAddonName(addon?.name)) {
      const option = options[0] || {};
      found.push({
        id: option.id ?? addon.id,
        addon_id: addon.id,
        name: addon.name,
        addon_name: addon.name,
        cost: industryCost(option, addon.cost),
      });
      return;
    }
    options.forEach((option) => {
      if (!isIndustryAddonName(option?.name)) return;
      found.push({
        id: option.id,
        addon_id: addon.id,
        name: option.name,
        addon_name: addon.name || option.name,
        cost: industryCost(option, 250),
      });
    });
  });
  return found;
}

export function decodeBrandMentionsQuery(encoded) {
  if (!encoded) return null;
  try {
    const parsed = JSON.parse(atob(encoded));
    const mentions = Array.isArray(parsed)
      ? parsed
      : parsed?.mentions || parsed?.data || [];
    if (!Array.isArray(mentions) || !mentions.length) return null;
    return {
      orderTitle: !Array.isArray(parsed)
        ? parsed?.order_title || parsed?.orderTitle || ""
        : "",
      mentions,
    };
  } catch {
    return null;
  }
}

function matchPlacementType(raw, placementTypes) {
  const placementName = String(raw?.placementType || raw?.placement || "").trim();
  const placementId = raw?.placement_type_id ?? raw?.placementTypeId ?? null;
  const matched = (placementTypes || []).find((type) => {
    if (placementId != null && String(type.id) === String(placementId)) return true;
    return normalizeCatalogName(type?.type) === normalizeCatalogName(placementName);
  });
  return matched?.type || placementName || "";
}

function matchIndustryAddon(raw, industryAddons) {
  const vertical = Array.isArray(raw?.verticals)
    ? raw.verticals[0]
    : raw?.industry || null;
  const verticalName = vertical?.name || (Array.isArray(raw?.verts) ? raw.verts[0] : "");
  return (industryAddons || []).find((item) => String(item.id) === String(vertical?.id))
    || (industryAddons || []).find((item) => normalizeCatalogName(item.name) === normalizeCatalogName(verticalName))
    || null;
}

export function isIncludeHyperlink(value) {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null || value === "") return false;
  const normalized = String(value).toLowerCase().trim();
  if (["0", "false", "no", "n", "off", "mention", "mention only", "no - mention only"].includes(normalized)) {
    return false;
  }
  return ["1", "true", "yes", "y", "on", "link", "add", "add a link", "yes - add a link"].includes(normalized);
}

export function parseIncludeHyperlink(raw = {}) {
  const value = [
    raw.includeHyperlink,
    raw.include_hyperlink,
    raw.includeLink,
    raw.include_link,
    raw.hyperlink,
    raw.addLink,
    raw.add_link,
  ].find((item) => item !== undefined && item !== null && item !== "");
  return isIncludeHyperlink(value);
}

export function mapWebsiteMention(raw, { basePrice, industryAddons, placementTypes } = {}) {
  const linkType = String(raw?.linkType || "").toLowerCase() === "dofollow" ? "dofollow" : "nofollow";
  const includeHyperlink = parseIncludeHyperlink(raw);
  const brandWebsite = String(raw?.brandWebsite || raw?.website || "").trim();
  const destinationUrl = includeHyperlink
    ? String(raw?.destinationUrl || raw?.dest || "").trim()
    : "";
  const industry = matchIndustryAddon(raw, industryAddons);
  const usp = raw?.usp || "";
  const targetQuery = raw?.targetQuery || raw?.topic || "";
  const brandDescription = raw?.brandDescription || raw?.desc || "";
  return {
    id: raw?.id || `m_${Math.random().toString(36).slice(2, 10)}`,
    linkType,
    includeHyperlink,
    placementType: matchPlacementType(raw, placementTypes),
    industryId: industry ? industry.id : null,
    brandName: String(raw?.brandName || raw?.brand || "").trim(),
    brandWebsite,
    briefOpen: Boolean(usp || brandDescription || targetQuery),
    destinationUrl,
    sameAsWebsite: includeHyperlink && destinationUrl !== "" && destinationUrl === brandWebsite,
    usp,
    targetQuery,
    brandDescription,
    complianceNotes: "",
    basePrice: Number(basePrice) || 0,
  };
}
